import React, {CSSProperties, FocusEvent, ReactNode, useState} from 'react'

import S1 from '../typography/S1.js'
import IconButton from '../buttons/IconButton.js'
import {buttonResetStyle} from '../buttons/styles.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {surfaceSizes} from './styles.js'

ensureFocusStyles()

interface DividerHeaderProps {
    title: string
    /** Starts collapsed */
    defaultHidden?: boolean
    style?: CSSProperties
    children?: ReactNode
    /** In edit mode: shows the save button instead of the edit button */
    edit?: boolean
    /** Shows an edit button while the header is hovered or has the focus */
    onClickEdit?: () => void
    onClickSave?: () => void
}

let nextId = 0

/**
 * A section with a title that collapses and expands its content. The title is a button (aria-expanded); collapsed
 * content stays mounted (keeps its state) but is hidden, so it can't get the focus. No outer margins.
 */
export default function DividerHeader({title, defaultHidden, style, children, edit, onClickEdit, onClickSave}: DividerHeaderProps) {
    const [show, setShow] = useState(!defaultHidden)
    const [hover, setHover] = useState(false)
    const [focusWithin, setFocusWithin] = useState(false)
    const [contentId] = useState(() => `divider-header-${++nextId}`)

    const onBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setFocusWithin(false)
        }
    }

    const headerStyle: CSSProperties = {
        width: '100%',
        height: 'fit-content',
        ...style
    }

    const toggleStyle: CSSProperties = {
        ...buttonResetStyle,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
    }

    // Collapsed: no height, and visibility hidden so nothing in it can be focused or read out
    const contentStyle: CSSProperties = {
        width: '100%',
        height: show ? 'fit-content' : 0,
        overflow: show ? 'visible' : 'hidden',
        visibility: show ? undefined : 'hidden'
    }

    return (
        <div
            style={headerStyle}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setFocusWithin(true)}
            onBlur={onBlur}
        >
            <div style={{display: 'flex', alignItems: 'center', marginBottom: surfaceSizes.dividerHeaderGap, height: surfaceSizes.dividerHeaderHeight}}>
                <button type='button' className={focusableClass} style={toggleStyle} aria-expanded={show} aria-controls={contentId} onClick={() => setShow(!show)}>
                    <S1>{title}</S1>
                    <i aria-hidden style={{marginLeft: surfaceSizes.dividerHeaderGap}} className={show ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'} />
                </button>

                <div style={{flex: 1}} />

                {(hover || focusWithin) && show && !edit && onClickEdit &&
                    <IconButton onClick={onClickEdit}>
                        <i className='mdi mdi-pencil' />
                    </IconButton>
                }

                {show && edit && onClickSave &&
                    <IconButton onClick={onClickSave}>
                        <i className='mdi mdi-check' />
                    </IconButton>
                }
            </div>

            <div id={contentId} style={contentStyle}>
                {children}
            </div>
        </div>
    )
}
